import { useEffect, useRef, useState } from "react";
import { api, imgUrl } from "@/lib/api";
import { socialLinksOf, socialIcon } from "@/lib/social";
import { useNavigate } from "react-router-dom";

export default function Footer() {
  const [settings, setSettings] = useState({});
  const [photos, setPhotos] = useState([]);
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const pausedRef = useRef(false);
  const draggingRef = useRef(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/portfolio").then((r) => {
      const all = r.data || [];
      const featured = all.filter((p) => p.featured);
      const pool = featured.length > 0 ? featured : all;
      const shuffled = [...pool].sort(() => Math.random() - 0.5);
      setPhotos(shuffled.slice(0, 12));
    }).catch(() => {});
  }, []);

  // duplicate for seamless loop
  const slides = photos.length > 0 ? [...photos, ...photos] : [];

  useEffect(() => {
    const track = trackRef.current;
    if (!track || slides.length === 0) return;
    let raf;
    let x = 0;
    const speed = 0.5; // px per frame ~ smooth
    const step = () => {
      if (!pausedRef.current && !draggingRef.current) {
        x += speed;
        const half = track.scrollWidth / 2;
        if (x >= half) x = 0;
        track.scrollLeft = x;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [slides.length]);

  // drag / swipe
  const startX = useRef(0);
  const startScroll = useRef(0);
  const onDown = (e) => {
    draggingRef.current = true;
    startX.current = e.touches ? e.touches[0].clientX : e.clientX;
    startScroll.current = trackRef.current.scrollLeft;
  };
  const onMove = (e) => {
    if (!draggingRef.current) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    trackRef.current.scrollLeft = startScroll.current - (x - startX.current);
  };
  const onUp = () => { draggingRef.current = false; };

  const links = socialLinksOf(settings);

  return (
    <footer data-testid="site-footer" className="border-t border-[#1A1A1A]/8">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 pb-32 lg:pb-20">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-8">Follow along</p>
        <div
          ref={trackRef}
          className="flex gap-3 mb-10 overflow-hidden select-none cursor-grab active:cursor-grabbing"
          data-testid="footer-carousel"
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = false; onUp(); }}
          onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp}
          onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
        >
          {slides.map((p, i) => (
            <div key={`${p.id}-${i}`} className="shrink-0 w-1/3 sm:w-1/6 aspect-square overflow-hidden bg-[#EFEFEC]">
              <img src={imgUrl(p.url)} alt={p.caption || "Photography by Bahare Salehnia"} loading="lazy" draggable="false"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 pointer-events-none" />
            </div>
          ))}
        </div>
        {links.length > 0 && (
          <div className="flex flex-wrap gap-x-6 gap-y-3" data-testid="footer-social-links">
            {links.map((l, i) => {
              const Icon = socialIcon(l.url);
              return (
                <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`footer-social-${i}`}
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#4A4A4A] hover:text-[#1A1A1A] transition-colors duration-300">
                  <Icon size={14} strokeWidth={1.5} /> {l.label || "Link"}
                </a>
              );
            })}
          </div>
        )}
        <div className="mt-16 pt-8 border-t border-[#1A1A1A]/8 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="font-serif text-2xl mb-2 italic">Bahare</p>
            <p className="text-xs text-[#7A7A7A] tracking-wide">Photographer · Realtor · Educator — Los Angeles</p>
          </div>
          <div className="flex gap-6 text-[11px] uppercase tracking-[0.2em] text-[#7A7A7A]">
            <button onClick={() => navigate("/photography")} className="hover:text-[#1A1A1A] transition-colors">Photography</button>
            <button onClick={() => navigate("/real-estate")} className="hover:text-[#1A1A1A] transition-colors">Real Estate</button>
            <button onClick={() => navigate("/learn")} className="hover:text-[#1A1A1A] transition-colors">Learn</button>
            <button onClick={() => navigate("/book")} data-testid="footer-book-link" className="hover:text-[#1A1A1A] transition-colors">Book</button>
          </div>
        </div>
        <p className="mt-10 text-[10px] tracking-[0.15em] text-[#B5B5B0] uppercase">© {new Date().getFullYear()} Bahare. All photographs are the property of the artist.</p>
      </div>
    </footer>
  );
}
